import { Link } from "@tanstack/react-router";
import { MapPin, Mail, ExternalLink, ArrowRight, Building2 } from "lucide-react";
import type { Business } from "../types/business";
import "../styles/components/business-card.css";
import "../styles/components/action-button.css";

interface BusinessCardProps {
  business: Business;
}

export default function BusinessCard({ business }: BusinessCardProps) {
  const primary = business.primary_color ?? "var(--color-primary)";
  const secondary = business.secondary_color ?? "var(--color-secondary)";

  const address = [
    business.unit_number && `Unit ${business.unit_number}`,
    business.house_number,
    business.street,
    business.city,
    business.region,
    business.zip,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <article
      className="business-card"
      style={
        {
          "--card-primary": primary,
          "--card-secondary": secondary,
        } as React.CSSProperties
      }
    >
      {/* Top accent bar */}
      <div className="business-card__accent-bar" />

      {/* Logo */}
      <div className="business-card__logo-wrapper">
        {business.business_image ? (
          <img
            className="business-card__logo-image"
            src={business.business_image}
            alt={`${business.business_name} logo`}
          />
        ) : (
          <div className="business-card__logo-placeholder">
            <Building2 size={28} />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="business-card__body">
        <h3 className="business-card__name">{business.business_name}</h3>

        {business.profile_summary && <p className="business-card__summary">{business.profile_summary}</p>}

        <div className="business-card__meta-info">
          {address && (
            <span className="business-card__meta-row">
              <MapPin size={14} />
              {address}
            </span>
          )}
          {business.contact_email && (
            <span className="business-card__meta-row">
              <Mail size={14} />
              {business.contact_email}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="business-card__actions">
        {business.website && (
          <a
            className="action-button action-button--outline"
            href={business.website}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink size={14} />
            Visit Site
          </a>
        )}
        <Link
          className="action-button action-button--primary"
          to="/business/$id"
          params={{ id: business?.id?.toString() ?? "" }}
        >
          View Details
          <ArrowRight size={14} />
        </Link>
      </div>
    </article>
  );
}
